import React, { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLivePoll, hasOpenRow } from "../lib/livePoll";
import { toast } from "sonner";
import { RefreshCw, Check, X, Clock, AlertTriangle } from "lucide-react";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";
import { UpstoxSyncPill } from "./UpstoxSyncPill";
import { Sparkline } from "./Sparkline";
import { useAuth } from "../auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 2026-08-28 — each section has its own existing manual-exit endpoint
// (already used by CandleBreakout.jsx / OIAutopilot.jsx / VWAPBounce.jsx on
// their own per-section pages), but this cross-section audit view never
// wired any of them in — an open live position here had no way to be
// closed except through the Upstox app directly. (Chat: "Exit option is
// needed in Live Orders")
const EXIT_ENDPOINT = {
  section5: "/candle-breakout/manual-exit",
  section6: "/oi-autopilot/manual-exit",
  section8: "/vwap-bounce/manual-exit",
  section4: "/sniper-autopilot/manual-exit",
  momentum_oi: "/momentum-oi/paper-trades/manual-exit",
};

const SECTION_LABEL = {
  section4: "S4 · Sniper",
  section5: "S5 · CB",
  section6: "S6 · OI",
  section8: "S8 · VWAP",
  momentum_oi: "Momentum OI",
};

const fmt = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", {
        maximumFractionDigits: d,
        minimumFractionDigits: d,
      });

const fmtISTDateTime = (iso) => {
  if (!iso) return "—";
  try {
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .format(new Date(s))
      .replace(",", "");
  } catch {
    return "—";
  }
};

const PHASE_STYLE = {
  FILLED: { color: "var(--bull)", icon: Check, label: "Filled" },
  OPEN: { color: "#FFB66E", icon: Clock, label: "Open" },
  REJECTED: { color: "var(--bear)", icon: X, label: "Rejected" },
  UNFILLED: { color: "var(--text-muted)", icon: X, label: "Not filled" },
  SOLD: { color: "var(--bull)", icon: Check, label: "Sold" },
  CLOSED: { color: "var(--text-muted)", icon: Check, label: "Closed" },
  UNKNOWN: { color: "var(--text-muted)", icon: AlertTriangle, label: "Unknown" },
};

const PhasePill = ({ phase }) => {
  const cfg = PHASE_STYLE[phase] || PHASE_STYLE.UNKNOWN;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border"
      style={{ color: cfg.color, borderColor: cfg.color }}
    >
      <Icon size={11} /> {cfg.label}
    </span>
  );
};

// Realized P&L summary for the day's closed round-trips (BUY filled + SOLD),
// used to drive the small change-amount/% + sparkline card, mirroring the
// Market Sentiment index cards.
//
// 2026-09-11 (chat: FORCEMOT showed "+₹25,470" here for a trade whose BUY
// never filled and whose SELL was REJECTED for insufficient margin — i.e.
// zero real money moved either way) — root cause: `!= null` treats a
// genuine `0` as present. Upstox's own order-confirmation response returns
// average_price=0 (not null) for an order that's been ACCEPTED but not yet
// FILLED (see vwap_bounce_autopilot.py's live_avg_fill_price assignment at
// BUY time), so any trade whose buy never filled carries live_avg_fill_price
// = 0, not null/undefined. The old filter let that 0 through as if it were
// a real ₹0 cost basis, so (sell_price - 0) × qty was counted as pure
// profit — the exact 509.40 × 50 = 25,470 shown. Now requires a genuinely
// positive fill price (matches the backend's own is_buy_filled() bar —
// live_trading_shared.py — for what counts as "actually bought"), and
// excludes any row with a live_sell_error (a rejected/failed sell moved no
// real money regardless of what live_sell_price was computed as).
const computeRealizedPnlSummary = (orders) => {
  const trades = (orders || [])
    .filter(
      (o) =>
        Number(o.live_avg_fill_price) > 0 &&
        o.live_sell_price != null &&
        !o.live_sell_error &&
        o.live_qty
    )
    .map((o) => ({
      ts: o.entered_at,
      invested: Number(o.live_avg_fill_price) * Number(o.live_qty),
      pnl: (Number(o.live_sell_price) - Number(o.live_avg_fill_price)) * Number(o.live_qty),
    }))
    .sort((a, b) => new Date(a.ts) - new Date(b.ts));

  if (trades.length === 0) return null;

  let running = 0;
  const series = trades.map((t) => (running += t.pnl));
  const totalPnl = running;
  const totalInvested = trades.reduce((s, t) => s + t.invested, 0);
  const pnlPct = totalInvested > 0 ? (totalPnl / totalInvested) * 100 : 0;

  return { series, totalPnl, pnlPct, count: trades.length };
};

// 2026-09-13 — extracted from the inline JSX that used to live directly in
// LiveOrderAuditLog's render (chat: "Admin should be able to see for all
// users (one table per user)"). Same exact markup/columns as before; just
// reusable so an admin viewer can render it once per user group while a
// regular user (who only ever gets their own rows from the backend now)
// still sees exactly one, unchanged.
const OrdersTable = ({ orders, exitingId, onManualExit }) => {
  if (!orders.length) {
    return (
      <div className="px-4 py-6 text-xs text-muted-3 text-center" data-testid="live-audit-empty">
        No live orders in this period.
      </div>
    );
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
          <th className="text-left px-4 py-2">Entry IST</th>
          <th className="text-left px-4 py-2">User</th>
          <th className="text-left px-4 py-2">Section</th>
          <th className="text-left px-4 py-2">Symbol</th>
          <th className="text-right px-4 py-2">Strike</th>
          <th className="text-right px-4 py-2">Qty</th>
          <th className="text-right px-4 py-2" title="Number of lots placed at entry (monthly-cycle sizing)">Lots</th>
          <th className="text-right px-4 py-2" title="Market price at scan time">Mkt ₹</th>
          <th className="text-right px-4 py-2" title="LIMIT BUY price = Market − 5%">Limit ₹</th>
          <th className="text-right px-4 py-2">Filled ₹</th>
          <th className="text-right px-4 py-2" title="SELL limit price = LTP + 0.25%">Sell ₹</th>
          <th className="text-right px-4 py-2">P&amp;L</th>
          <th className="text-right px-4 py-2">Phase</th>
          <th className="text-right px-4 py-2">Exit</th>
          <th className="text-left px-4 py-2">Order IDs</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o, i) => (
          <tr key={o.id || i} data-testid={`live-audit-row-${i}`} className="border-b border-grid hover:bg-white/[0.02]">
            <td className="px-4 py-2 text-muted-2 font-mono-data whitespace-nowrap">
              {fmtISTDateTime(o.entered_at)}
            </td>
            <td className="px-4 py-2 font-mono-data text-[11px]">
              {o.user_name || <span className="text-muted-3">—</span>}
            </td>
            <td className="px-4 py-2 text-muted-2 font-mono-data uppercase">
              {SECTION_LABEL[o.section] || o.section}
            </td>
            <td className="px-4 py-2 font-semibold">{o.symbol}</td>
            <td className="px-4 py-2 text-right font-mono-data" title={o.expiry ? `Expiry ${o.expiry}` : undefined}>
              {o.atm_strike ? `${o.atm_strike} ${o.option_type || ""}`.trim() : "—"}
            </td>
            <td className="px-4 py-2 text-right font-mono-data text-muted-2">{o.live_qty ?? "—"}</td>
            <td className="px-4 py-2 text-right font-mono-data text-muted-2">{o.lots ?? "—"}</td>
            <td className="px-4 py-2 text-right font-mono-data text-muted-2">{o.current_market_price ? `₹${fmt(o.current_market_price)}` : "—"}</td>
            <td className="px-4 py-2 text-right font-mono-data" style={{ color: "var(--bull)" }}
                title={o.buy_buffer_pct != null ? `BUY LIMIT = market − ${o.buy_buffer_pct}%` : undefined}>
              {o.limit_order_price ? `₹${fmt(o.limit_order_price)}` : (o.live_buy_price ? `₹${fmt(o.live_buy_price)}` : "—")}
            </td>
            <td className="px-4 py-2 text-right font-mono-data text-muted-2">
              {o.live_avg_fill_price ? `₹${fmt(o.live_avg_fill_price)}` : "—"}
            </td>
            <td className="px-4 py-2 text-right font-mono-data">{o.live_sell_price ? `₹${fmt(o.live_sell_price)}` : "—"}</td>
            <td className="px-4 py-2 text-right font-mono-data" style={{ color: (o.final_pnl_pct ?? o.pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
              {o.final_pnl_pct != null
                ? `${o.final_pnl_pct >= 0 ? "+" : ""}${fmt(o.final_pnl_pct)}%`
                : o.pnl_pct != null
                ? `${o.pnl_pct >= 0 ? "+" : ""}${fmt(o.pnl_pct)}%`
                : "—"}
              {/* 2026-10-07 — rupee P&L under the %: net of charges when the
                  backend has it, else gross from the real buy/sell fills. */}
              {(() => {
                if (o.phase === "UNFILLED" || o.phase === "REJECTED") return null;
                const rs = o.net_pnl != null
                  ? Number(o.net_pnl)
                  : (Number(o.live_avg_fill_price) > 0 && o.live_sell_price != null && !o.live_sell_error && o.live_qty)
                  ? (Number(o.live_sell_price) - Number(o.live_avg_fill_price)) * Number(o.live_qty)
                  : null;
                return rs == null || Number.isNaN(rs) ? null : (
                  <div className="text-[10px]" title={o.net_pnl != null ? "Net of estimated charges" : "Gross, from broker fills"}>
                    {rs >= 0 ? "+" : "−"}₹{fmt(Math.abs(rs), 0)}{o.net_pnl != null ? " net" : ""}
                  </div>
                );
              })()}
            </td>
            <td className="px-4 py-2 text-right"><PhasePill phase={o.phase} /></td>
            <td className="px-4 py-2 text-right">
              {o.status === "OPEN" && EXIT_ENDPOINT[o.section] ? (
                <button
                  data-testid={`live-audit-exit-${i}`}
                  onClick={() => onManualExit(o)}
                  disabled={exitingId === o.id}
                  title={o.live_trade ? "Manual Exit — validates position then places SELL on Upstox" : "Manual Exit — paper close at current LTP"}
                  className="text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border border-grid hover:bg-white/5 disabled:opacity-50"
                  style={{ color: "var(--bear)", borderColor: "var(--bear)" }}
                >
                  {exitingId === o.id ? "…" : "Exit"}
                </button>
              ) : o.exit_label ? (
                <span className="text-[10px] font-mono-data text-muted-2 whitespace-nowrap">{o.exit_label}</span>
              ) : (
                <span className="text-muted-3">—</span>
              )}
            </td>
            <td className="px-4 py-2 font-mono-data text-[10px] text-muted-3">
              <div className="flex flex-col">
                {o.live_buy_order_id && (
                  <span title="Buy order ID">B · {o.live_buy_order_id}</span>
                )}
                {o.live_sell_order_id && (
                  <span title="Sell order ID" style={{ color: "var(--bull)" }}>S · {o.live_sell_order_id}</span>
                )}
                {o.live_sell_error && (
                  <span title={o.live_sell_error} style={{ color: "var(--bear)" }}>
                    Sell err · {o.live_sell_error.slice(0, 40)}
                  </span>
                )}
                {o.chase_status && (
                  <span title="Buy-price chase result">
                    Chase · {String(o.chase_status).replace(/_/g, " ").toLowerCase()}
                    {o.chase_steps ? ` (${o.chase_steps} steps)` : ""}
                  </span>
                )}
                {o.sell_skipped_reason && (
                  <span style={{ color: "var(--text-muted)" }}>
                    Sell skipped · {o.sell_skipped_reason}
                  </span>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const LiveOrderAuditLog = () => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(1);
  const [convergent, setConvergent] = useState(null);
  const [collapsed, toggle] = useCollapsed("live-order-audit", false);
  const [exitingId, setExitingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/live-orders/audit?days=${days}`);
      setData(r.data);
      try {
        const c = await axios.get(`${API}/live-orders/cross-section-alerts?threshold=3`);
        setConvergent(c.data);
      } catch { /* best-effort */ }
    } catch {
      toast.error("Failed to load live-order audit log");
    } finally {
      setLoading(false);
    }
  }, [days]);

  // Mirrors the established onManualExit pattern from CandleBreakout.jsx /
  // OIAutopilot.jsx / VWAPBounce.jsx — same confirm-dialog wording, same
  // endpoint per section, just routed from this cross-section table.
  const onManualExit = async (o) => {
    const endpoint = EXIT_ENDPOINT[o.section];
    if (!endpoint) {
      toast.error(`No manual-exit endpoint for section "${o.section}"`);
      return;
    }
    if (!window.confirm(
      `Manually exit ${o.symbol}?\n\nThis will close the trade at the latest LTP. ` +
      `Live trades will trigger a SELL on Upstox (only if a position still exists).`
    )) return;
    setExitingId(o.id);
    try {
      const r = await axios.post(`${API}${endpoint}`, {
        trade_id: o.id,
        action: "MANUAL_EXIT",
      });
      const trade = r?.data?.trade;
      const pnl = trade?.final_pnl_pct;
      const skipped = trade?.sell_skipped_reason === "no_position";
      toast.success(
        `${o.symbol}: Manual exit${pnl != null ? ` · P&L ${pnl >= 0 ? "+" : ""}${pnl}%` : ""}` +
        (skipped ? " · sell skipped (no Upstox position)" : "")
      );
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Manual exit failed";
      toast.error(`${o.symbol}: ${detail}`);
    } finally {
      setExitingId(null);
    }
  };

  // 2026-10-16 — 10 s while any live row is OPEN, 30 s otherwise (lib/livePoll.js).
  const daysRef = useRef(days);
  daysRef.current = days;
  const tick = async () => {
    const d = daysRef.current;
    try {
      const r = await axios.get(`${API}/live-orders/audit?days=${d}`);
      if (daysRef.current === d) setData(r.data);
      try {
        const c = await axios.get(`${API}/live-orders/cross-section-alerts?threshold=3`);
        setConvergent(c.data);
      } catch { /* best-effort */ }
    } catch {
      toast.error("Failed to load live-order audit log");
    } finally {
      setLoading(false);
    }
  };

  const counts = data?.counts || {};
  const orders = data?.orders || [];
  useLivePoll(tick, hasOpenRow(orders), [days]);
  const pnlSummary = useMemo(() => computeRealizedPnlSummary(orders), [orders]);

  return (
    <section className="space-y-4" data-testid="live-order-audit-section">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            07 / Live Order Audit Log
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Today&apos;s Live Orders
          </h2>
          <p className="text-xs text-muted-2 mt-1 max-w-3xl">
            End-of-day reconciliation feed. Aggregates every <strong>live Upstox order</strong> placed
            across Section 5 (Candle Breakout), Section 6 (OI Autopilot), and Section 8 (VWAP Bounce),
            with broker order-id, status, filled qty, average fill price and sell-leg payloads.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <UpstoxSyncPill />
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="live-audit-days"
            className="bg-transparent border border-grid px-2 py-1.5 text-[10px] uppercase tracking-wider font-mono-data"
          >
            <option value={1}>Today</option>
            <option value={3}>Last 3 days</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
          </select>
          {!collapsed && (
            <button
              data-testid="live-audit-refresh"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-wider font-mono-data px-3 py-1.5 border border-grid hover:bg-white/5 disabled:opacity-50"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          )}
          <CollapseToggle collapsed={collapsed} onToggle={toggle} testId="live-audit-collapse" />
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Realized P&L summary — change amount + % with a small sparkline,
              styled like the Market Sentiment index cards. */}
          {pnlSummary && (
            <div
              className="border-grid surface p-4 flex items-center justify-between gap-4"
              data-testid="live-orders-pnl-summary"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data mb-1">
                  Realized P&amp;L Today · {pnlSummary.count} closed trade
                  {pnlSummary.count === 1 ? "" : "s"}
                </div>
                <div
                  className="font-mono-data text-2xl md:text-3xl font-semibold tracking-tight"
                  style={{ color: pnlSummary.totalPnl >= 0 ? "var(--bull)" : "var(--bear)" }}
                  data-testid="live-orders-pnl-amount"
                >
                  {pnlSummary.totalPnl >= 0 ? "+" : "-"}₹{fmt(Math.abs(pnlSummary.totalPnl), 0)}
                  <span className="text-sm ml-2 font-normal">
                    ({pnlSummary.pnlPct >= 0 ? "+" : ""}
                    {fmt(pnlSummary.pnlPct)}%)
                  </span>
                </div>
              </div>
              <Sparkline
                data={pnlSummary.series}
                positive={pnlSummary.totalPnl >= 0}
                width={90}
                height={28}
              />
            </div>
          )}

          {/* Cross-section convergence panel — high-conviction signals */}
          {convergent?.convergent?.length > 0 && (
            <div
              className="border-grid surface px-4 py-3 space-y-2"
              data-testid="cross-section-convergence-panel"
              style={{ borderColor: "var(--bull)", background: "rgba(0,230,118,0.05)" }}
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-mono-data" style={{ color: "var(--bull)" }}>
                <AlertTriangle size={12} /> Cross-section convergence
                <span className="text-muted-3">· {convergent.convergent.length} symbol(s) picked by ≥ {convergent.threshold} sections</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {convergent.convergent.map((row) => (
                  <span
                    key={row.symbol}
                    className="inline-flex items-center gap-2 px-2 py-1 border text-[10px] font-mono-data"
                    style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
                    data-testid={`convergent-symbol-${row.symbol}`}
                    title={`Sections: ${row.sections.join(", ")}`}
                  >
                    <strong>{row.symbol}</strong>
                    <span className="text-muted-3">{row.count}× · {row.sections.join("·")}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Counts strip */}
          <div className="border-grid surface px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-[10px] uppercase tracking-wider font-mono-data">
            <span className="text-muted-3">Day · {data?.day || "—"}</span>
            <span>Total · <strong style={{ color: "var(--text-primary)" }}>{counts.total ?? 0}</strong></span>
            <span style={{ color: "var(--bull)" }}>Filled · {counts.filled ?? 0}</span>
            <span style={{ color: "#FFB66E" }}>Open · {counts.open ?? 0}</span>
            <span style={{ color: "var(--bear)" }}>Rejected · {counts.rejected ?? 0}</span>
            <span style={{ color: "var(--text-muted)" }}>Not filled · {counts.unfilled ?? 0}</span>
            <span style={{ color: "var(--bull)" }}>Sold · {counts.sold ?? 0}</span>
            <span style={{ color: "var(--text-muted)" }}>Closed · {counts.closed ?? 0}</span>
          </div>

          {/* Orders table(s) — 2026-09-13: admins viewing `by_user` get one
              table per user (chat: "Admin should be able to see for all
              users (one table per user)"); everyone else's `by_user` is
              null (backend only fills it in for an ADMIN viewer), so they
              keep the single table they always had, just scoped to their
              own rows server-side now. */}
          {isAdmin && data?.by_user ? (
            <div className="space-y-4">
              {data.by_user.map((grp) => (
                <div key={grp.user_id || "unknown"} className="border-grid surface overflow-x-auto" data-testid={`live-audit-user-group-${grp.user_id || "unknown"}`}>
                  <div className="px-4 py-2 border-b border-grid text-[11px] uppercase tracking-wider font-mono-data text-muted-1 flex items-center gap-2">
                    {grp.user_name}
                    <span className="text-muted-3 normal-case tracking-normal">· {grp.counts?.total ?? grp.orders.length} order{grp.orders.length === 1 ? "" : "s"}</span>
                  </div>
                  <OrdersTable orders={grp.orders} exitingId={exitingId} onManualExit={onManualExit} />
                </div>
              ))}
              {data.by_user.length === 0 && (
                <div className="border-grid surface px-4 py-6 text-xs text-muted-3 text-center">
                  No live orders today.
                </div>
              )}
            </div>
          ) : (
            <div className="border-grid surface overflow-x-auto">
              <OrdersTable orders={orders} exitingId={exitingId} onManualExit={onManualExit} />
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default LiveOrderAuditLog;